/* WSH · Stem mixer · sample-accurate multi-source playback (Phase C.1)
   Generic component: emits `wsh-mixer:*` document events. Host supplies
   getSharedCtx / pauseAllOthers / fysTrack via opts. Styles at wsh-mixer.css.

   Audio path (Phase C.B.4 — single-AudioContext regime):
     - opts.getSharedCtx() returns the ONE page-scoped AudioContext.
     - Stems decoded with decodeAudioData → AudioBufferSourceNodes scheduled at
       t0 for sample-accurate lane sync.
     - Lane VISUALS via WaveSurfer 7 with backend: 'MediaElement' + `media: null`
       — no per-lane WebAudio context, no null-URL fetch, no stem-URL request.
       Peaks are supplied by peaks() with a precomputed duration so WaveSurfer
       has no reason to touch audio at all.

   Rate math (Phase C.B.3):
     Convert sample counts to seconds using session.source_sample_rate (44100
     for Country stems), not the AudioContext sampleRate. iOS runs the ctx at
     48 kHz; comparing raw sample counts (source-rate) to the buffer's own
     sample-rate would misjudge trimmed vs untrimmed.

   Seek (Phase C.B.5):
     play() no longer overwrites startOffset unless we're starting from a hard
     stop. Clicking a waveform sets pendingSeek; play() consumes it. */
(function () {
  'use strict';

  const RAMP_MS_MUTE = 15;
  const RAMP_MS_PLAY_IN = 120;
  const RAMP_MS_PAUSE_OUT = 250;

  class Mixer {
    constructor(container, session, opts) {
      this.container = container;
      this.session = session;
      this.opts = opts || {};
      this.ctx = null;
      this.buffers = [];
      this.sources = [];
      this.laneGains = [];
      this.masterGain = null;
      this.state = {
        preset: 'full',
        muted: new Set(),
        soloed: new Set(),
        playing: false,
        startedAtCtx: 0,
        startOffset: 0
      };
      this.pendingSeek = null;  // seconds, set by seekTo(); consumed by play()
      this.laneWs = [];
      this.rafId = 0;
      this.build();
      document.addEventListener('product:audio:pause', this.pauseFromExternal.bind(this));
      document.addEventListener('soundbar:pause', this.pauseFromExternal.bind(this));
      document.addEventListener('wsh-fys:audio:pause', this.pauseFromExternal.bind(this));
    }

    pauseFromExternal() { if (this.state.playing) this.pause(); }

    build() {
      const s = this.session;
      const root = document.createElement('div');
      root.className = 'wsh-mixer';
      root.innerHTML = [
        // Top row: play + time + host-injected controls slot + status.
        // The internal FULL BAND / JUST DRUMS pill has been retired — the FYS
        // layer injects three answer buttons into [data-controls-slot] instead,
        // and drives .applyPreset() through the public API (Phase C.2 item 4).
        '<div class="wsh-mixer__top">',
        '  <button type="button" class="wsh-mixer__play" data-play aria-label="Play the groove">',
        '    <svg width="18" height="18" viewBox="0 0 24 24" data-icon><path d="M8 5v14l11-7z"/></svg>',
        '  </button>',
        '  <span class="wsh-mixer__time" data-time>0:00 / 0:00</span>',
        '  <div class="wsh-mixer__controls" data-controls-slot></div>',
        '  <span class="wsh-mixer__status" data-status>Loading stems…</span>',
        '</div>',
        '<div class="wsh-mixer__lanes"></div>'
      ].join('');
      this.container.appendChild(root);
      this.root = root;
      this.lanesRoot = root.querySelector('.wsh-mixer__lanes');
      this.playBtn = root.querySelector('[data-play]');
      this.timeEl = root.querySelector('[data-time]');
      this.statusEl = root.querySelector('[data-status]');
      this.controlsSlot = root.querySelector('[data-controls-slot]');

      // Real duration BEFORE first play (Phase C.2 item 4): source_sample_rate
      // + length_samples give us the loop's true duration in seconds without
      // any decode; render as "0:00 / 0:11" pre-play so the readout is honest.
      {
        const sr = s.source_sample_rate || 44100;
        const initDur = s.length_samples ? (s.length_samples / sr) : 0;
        if (this.timeEl && initDur > 0) this.timeEl.textContent = '0:00 / ' + fmtTime(initDur);
      }

      this.playBtn.addEventListener('click', () => {
        if (this.state.playing) this.pause();
        else this.play();
      });

      // Render lanes — WaveSurfer visuals only, no WebAudio backend, no null fetch
      s.lanes.forEach((lane, i) => {
        const laneEl = document.createElement('div');
        laneEl.className = 'wsh-mixer__lane';
        laneEl.dataset.laneIdx = i;
        laneEl.innerHTML = [
          '<span class="wsh-mixer__lane-label">' + escape(lane.label) + '</span>',
          '<div class="wsh-mixer__lane-wave"><div class="wsh-mixer__lane-wave-inner"></div></div>',
          '<div class="wsh-mixer__lane-btns">',
          '  <button type="button" class="wsh-mixer__ms-btn" data-mute aria-label="Mute ' + escape(lane.label) + '">M</button>',
          '  <button type="button" class="wsh-mixer__ms-btn" data-solo aria-label="Solo ' + escape(lane.label) + '">S</button>',
          '</div>'
        ].join('');
        this.lanesRoot.appendChild(laneEl);

        // Peaks-only visual (no <audio>, no stem fetch by WaveSurfer)
        if (window.WaveSurfer) {
          try {
            const sr = this.session.source_sample_rate || 44100;
            const dur = this.session.length_samples ? (this.session.length_samples / sr) : undefined;
            // Closing-fix item 10 (brief §3.B.6): waveform colours resolve
            // from --fys-on-dark via getComputedStyle — no literals.
            const _onDarkHex = (getComputedStyle(document.body).getPropertyValue('--fys-on-dark').trim() || '#F0E8D5').replace('#', '');
            const _h = (_onDarkHex.length === 3) ? _onDarkHex.split('').map(function (c) { return c + c; }).join('') : _onDarkHex;
            const _onDarkRgb = parseInt(_h.substr(0,2),16) + ',' + parseInt(_h.substr(2,2),16) + ',' + parseInt(_h.substr(4,2),16);
            const ws = window.WaveSurfer.create({
              container: laneEl.querySelector('.wsh-mixer__lane-wave-inner'),
              waveColor: 'rgba(' + _onDarkRgb + ',0.3)',
              progressColor: 'rgb(' + _onDarkRgb + ')',
              cursorColor: 'transparent',
              height: 32,
              barWidth: 2,
              barGap: 1,
              barRadius: 1,
              normalize: true,
              interact: true,
              backend: 'MediaElement',
              media: null,
              duration: dur,
              peaks: null  // filled once peaks arrive
            });
            // Fetch peaks separately (no audio load).
            // Bug 7a fix (2026-09-29 eve): use the peaks file's own URL from
            // the lane record, not a derivation from the stem URL. Shopify's
            // Files API assigns independent UUIDs to every upload, so
            // `stem.replace('.mp3', '.mp3.json')` always 404s on the CDN.
            const peaksUrl = lane.peaks_url || (lane.stem_url || '').replace('.mp3', '.mp3.json');
            fetch(peaksUrl)
              .then(r => r.ok ? r.json() : null)
              .then(pk => {
                if (!pk) { this._markVisualReady(); return; }
                const arr = pk.data || pk;
                try { ws.load(null, [arr], dur); } catch { /* noop */ }
                this._markVisualReady();
              })
              .catch(() => this._markVisualReady());
            ws.on('interaction', (t) => {
              if (typeof t === 'number' && this.getBufDuration() > 0) {
                this.seekTo(t * this.getBufDuration());
              }
            });
            this.laneWs[i] = ws;
          } catch { this._markVisualReady(); }
        } else {
          this._markVisualReady();
        }

        laneEl.querySelector('[data-mute]').addEventListener('click', () => this.toggleMute(i));
        laneEl.querySelector('[data-solo]').addEventListener('click', () => this.toggleSolo(i));
      });

      this.decodePromise = null;
      this._visualsReady = 0;
      this._visualsTarget = s.lanes.length;
      // Belt-and-braces
      setTimeout(() => { if (this._visualsReady < this._visualsTarget && this.statusEl) this.statusEl.textContent = 'Ready'; }, 5000);
    }

    _markVisualReady() {
      this._visualsReady++;
      if (this._visualsReady >= this._visualsTarget && this.statusEl) this.statusEl.textContent = 'Ready';
    }

    async ensureAudio() {
      if (this.ctx && this.buffers.length === this.session.lanes.length) return true;
      if (this.decodePromise) return this.decodePromise;
      this.decodePromise = (async () => {
        try {
          // Shared context — get from host or fall back to page-scope singleton
          if (typeof this.opts.getSharedCtx === 'function') {
            this.ctx = this.opts.getSharedCtx();
          }
          if (!this.ctx) {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
          }
          if (this.ctx.state === 'suspended') try { await this.ctx.resume(); } catch { /* noop */ }
          this.masterGain = this.ctx.createGain();
          this.masterGain.gain.value = 0;
          this.masterGain.connect(this.ctx.destination);
          const results = await Promise.all(this.session.lanes.map(async (lane) => {
            const r = await fetch(lane.stem_url);
            const ab = await r.arrayBuffer();
            return await new Promise((res, rej) => {
              this.ctx.decodeAudioData(ab, res, rej);
            });
          }));
          this.buffers = results;
          this.laneGains = results.map(() => {
            const g = this.ctx.createGain();
            g.gain.value = 1;
            g.connect(this.masterGain);
            return g;
          });
          const dur = this.getBufDuration() || 0;
          if (this.timeEl) this.timeEl.textContent = '0:00 / ' + fmtTime(dur);
          if (this.statusEl) this.statusEl.textContent = 'Ready';
          return true;
        } catch (err) {
          if (this.statusEl) this.statusEl.textContent = 'Load failed';
          return false;
        }
      })();
      return this.decodePromise;
    }

    getBufDuration() {
      if (!this.buffers.length) return 0;
      // Rate math in SOURCE-rate space (Phase C.B.3). iOS ctx.sampleRate == 48000
      // but source is 44100; using ctx.sampleRate would misjudge trimmed/untrimmed.
      const sr = this.session.source_sample_rate || 44100;
      const src = this.session.length_samples;
      if (src) return src / sr;
      return this.buffers[0].duration;
    }

    getBrowserLoopParams() {
      const b = this.buffers[0];
      if (!b) return { startOffset: 0, loopStart: 0, loopEnd: 0 };
      const sr = this.session.source_sample_rate || 44100;
      const src = this.session.length_samples || 0;
      const delay = this.session.encoder_delay_samples || 0;
      const padding = this.session.padding_samples || 0;
      if (src === 0) return { startOffset: 0, loopStart: 0, loopEnd: b.duration };
      // Decide trimmed vs untrimmed by SECONDS (Phase C.B.3) — b.duration is
      // in the context's own timebase; source-samples convert to source-seconds.
      const srcSec = src / sr;
      const trimmedSec = Math.abs(b.duration - srcSec) < 0.005;  // 5 ms tolerance
      if (trimmedSec) {
        return { startOffset: 0, loopStart: 0, loopEnd: srcSec };
      }
      // Safari: header not trimmed → skip delay, stop before padding (in ctx seconds)
      // Delay/padding are in SOURCE samples — convert to ctx-seconds via source-rate.
      return {
        startOffset: delay / sr,
        loopStart: delay / sr,
        loopEnd: (delay + src) / sr
      };
    }

    play() {
      this.opts.pauseAllOthers && this.opts.pauseAllOthers('mixer');
      document.dispatchEvent(new CustomEvent('wsh-mixer:play'));
      // P2 Fix §A5 (round 2): tag each play invocation so a pause() fired
      // before the stems finish loading can cancel the pending start. Without
      // this, the user could land on the format step, press play, nav away
      // before the fetch+decode completes, and the mixer would start seconds
      // later on an off-screen step.
      const playId = (this._playId = (this._playId || 0) + 1);
      this.ensureAudio().then((ok) => {
        if (!ok) return;
        if (playId !== this._playId) return;      // cancelled by a later pause()
        this.stopAllSources();
        const params = this.getBrowserLoopParams();
        const t0 = this.ctx.currentTime + 0.05;
        this.state.startedAtCtx = t0;
        // Consume pendingSeek if set (Phase C.B.5 fix — don't clobber it on play())
        if (this.pendingSeek !== null && this.pendingSeek !== undefined) {
          const s = Math.max(params.loopStart, Math.min(params.loopEnd, params.loopStart + (this.pendingSeek % (params.loopEnd - params.loopStart))));
          this.state.startOffset = s;
          this.pendingSeek = null;
        } else if (!this.state.startOffset) {
          this.state.startOffset = params.startOffset;
        }
        this.sources = this.buffers.map((buf, i) => {
          const src = this.ctx.createBufferSource();
          src.buffer = buf;
          src.loop = true;
          src.loopStart = params.loopStart;
          src.loopEnd = params.loopEnd || buf.duration;
          src.connect(this.laneGains[i]);
          src.start(t0, this.state.startOffset);
          return src;
        });
        this.masterGain.gain.cancelScheduledValues(this.ctx.currentTime);
        this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
        this.masterGain.gain.linearRampToValueAtTime(1, t0 + RAMP_MS_PLAY_IN / 1000);
        this.state.playing = true;
        this.updatePlayIcon();
        this.tickPlayhead();
        this.opts.fysTrack && this.opts.fysTrack('fys:mixer:play', {});
      });
    }

    pause() {
      // P2 Fix §A5 (round 2): invalidate any in-flight play() promise FIRST
      // so a slow fetch/decode can't start sources after the step was left.
      this._playId = (this._playId || 0) + 1;
      if (!this.state.playing) return;
      const now = this.ctx.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.linearRampToValueAtTime(0, now + RAMP_MS_PAUSE_OUT / 1000);
      const s = this.sources.slice();
      setTimeout(() => {
        s.forEach(src => { try { src.stop(); } catch { /* noop */ } });
      }, RAMP_MS_PAUSE_OUT + 20);
      this.state.playing = false;
      this.updatePlayIcon();
      cancelAnimationFrame(this.rafId);
      document.dispatchEvent(new CustomEvent('wsh-mixer:pause'));
      this.opts.fysTrack && this.opts.fysTrack('fys:mixer:pause', {});
    }

    stopAllSources() {
      this.sources.forEach(src => { try { src.stop(); } catch { /* noop */ } });
      this.sources = [];
    }

    seekTo(t) {
      // Store as pending; play() picks it up (Phase C.B.5 fix)
      this.pendingSeek = t;
      const wasPlaying = this.state.playing;
      if (wasPlaying) {
        this.pause();
        setTimeout(() => this.play(), RAMP_MS_PAUSE_OUT + 40);
      }
      document.dispatchEvent(new CustomEvent('wsh-mixer:seek', { detail: { t: t } }));
    }

    tickPlayhead() {
      if (!this.state.playing) return;
      const dur = this.getBufDuration();
      if (dur > 0) {
        const params = this.getBrowserLoopParams();
        const elapsed = this.ctx.currentTime - this.state.startedAtCtx;
        const loopDur = params.loopEnd - params.loopStart;
        const pos = ((this.state.startOffset - params.loopStart) + elapsed) % loopDur;
        const posSec = pos + params.loopStart;
        const frac = (posSec - params.loopStart) / loopDur;
        this.laneWs.forEach(ws => {
          try { if (ws && typeof ws.seekTo === 'function') ws.seekTo(Math.max(0, Math.min(0.999, frac))); } catch { /* noop */ }
        });
        if (this.timeEl) this.timeEl.textContent = fmtTime(pos) + ' / ' + fmtTime(dur);
      }
      this.rafId = requestAnimationFrame(() => this.tickPlayhead());
    }

    applyPreset(preset) {
      // 'both' is semantically identical to 'full' — every lane on. It's a
      // distinct answer-button so the FYS layer can remember which one the
      // user tapped, but the mix state is the same.
      if (preset === 'full' || preset === 'both') {
        this.state.preset = preset;
        this.state.muted.clear();
        this.state.soloed.clear();
      } else if (preset === 'drums') {
        this.state.preset = 'drums';
        this.state.muted.clear();
        this.state.soloed.clear();
        this.session.lanes.forEach((lane, i) => { if (lane.is_drums) this.state.soloed.add(i); });
      }
      this.refreshLaneGains();
      this.refreshUI();
      document.dispatchEvent(new CustomEvent('wsh-mixer:preset', { detail: { preset: preset } }));
      this.opts.fysTrack && this.opts.fysTrack('fys:mixer:preset', { preset: preset });
    }

    toggleMute(i) {
      if (this.state.muted.has(i)) this.state.muted.delete(i);
      else this.state.muted.add(i);
      this.detectPresetDrift();
      this.refreshLaneGains();
      this.refreshUI();
      document.dispatchEvent(new CustomEvent('wsh-mixer:lane', { detail: { lane: i, mute: this.state.muted.has(i) } }));
      this.opts.fysTrack && this.opts.fysTrack('fys:mixer:lane', { lane: i, mute: this.state.muted.has(i) });
    }
    toggleSolo(i) {
      if (this.state.soloed.has(i)) this.state.soloed.delete(i);
      else this.state.soloed.add(i);
      this.detectPresetDrift();
      this.refreshLaneGains();
      this.refreshUI();
      document.dispatchEvent(new CustomEvent('wsh-mixer:lane', { detail: { lane: i, solo: this.state.soloed.has(i) } }));
      this.opts.fysTrack && this.opts.fysTrack('fys:mixer:lane', { lane: i, solo: this.state.soloed.has(i) });
    }
    detectPresetDrift() {
      const drumsIdx = this.session.lanes.map((l, i) => l.is_drums ? i : -1).filter(x => x >= 0);
      const soloArr = Array.from(this.state.soloed).sort();
      if (this.state.muted.size === 0 && this.state.soloed.size === 0) this.state.preset = 'full';
      else if (this.state.muted.size === 0 && soloArr.join(',') === drumsIdx.join(',')) this.state.preset = 'drums';
      else this.state.preset = null;
    }

    refreshLaneGains() {
      if (!this.ctx || !this.laneGains.length) return;
      const anySolo = this.state.soloed.size > 0;
      const t = this.ctx.currentTime;
      this.laneGains.forEach((g, i) => {
        let target = 1;
        if (this.state.muted.has(i)) target = 0;
        else if (anySolo && !this.state.soloed.has(i)) target = 0;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(target, t + RAMP_MS_MUTE / 1000);
      });
    }

    refreshUI() {
      // Preset buttons are host-injected (FYS layer) — no internal loop here.
      // The FYS layer subscribes to `wsh-mixer:preset` if it needs to react to
      // preset changes (e.g. sync answer-button selection after a manual M/S
      // change that resets state.preset). Manual M/S drift is NOT a preset
      // change — item 4 says answer selection stays put after M/S edits.
      this.lanesRoot.querySelectorAll('.wsh-mixer__lane').forEach((el, i) => {
        el.classList.toggle('is-muted', this.state.muted.has(i) || (this.state.soloed.size > 0 && !this.state.soloed.has(i)));
        const mBtn = el.querySelector('[data-mute]');
        const sBtn = el.querySelector('[data-solo]');
        if (mBtn) mBtn.classList.toggle('is-active', this.state.muted.has(i));
        if (sBtn) sBtn.classList.toggle('is-active', this.state.soloed.has(i));
      });
    }

    updatePlayIcon() {
      const icon = this.playBtn.querySelector('[data-icon]');
      if (!icon) return;
      if (this.state.playing) icon.innerHTML = '<path d="M6 5h4v14H6zM14 5h4v14h-4z"/>';
      else icon.innerHTML = '<path d="M8 5v14l11-7z"/>';
    }
  }

  function fmtTime(s) {
    s = Math.max(0, s | 0);
    const m = (s / 60) | 0;
    return m + ':' + String(s % 60).padStart(2, '0');
  }
  function escape(s) {
    return String(s || '').replace(/[<>&"']/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  window.WshMixer = {
    mount: function (container, session, opts) {
      if (window.__wshMixerInstance) {
        try { window.__wshMixerInstance.pause(); } catch { /* noop */ }
      }
      const inst = new Mixer(container, session, opts);
      window.__wshMixerInstance = inst;
      return inst;
    }
  };
})();
